import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_COMPANY = {
  name: "Bravura Packers Pvt Ltd",
  address: "Jaipur, Rajasthan, India",
  pan: "",
  tan: "",
  gstin: "",
  pf_code: "",
  esi_code: "",
  establishment_id: "",
  logo_url: "",
  signature_url: "",
};

const DEFAULT_SALARY = {
  basic_pct: 50,
  hra_pct: 30,
  conveyance_pct: 20,
  days_in_month_default: 30,
};

const DEFAULT_STATUTORY = {
  pf_wage_cap: 15000,
  esi_threshold: 21000,
  pf_employee_pct: 12,
  eps_pct: 8.33,
  epf_pct: 3.67,
  pf_admin_pct: 0.5,
  edli_pct: 0.5,
  esi_employee_pct: 0.75,
  esi_employer_pct: 3.25,
};

const DEFAULT_ATTENDANCE = {
  late_grace_minutes: 15,
  free_lates_per_month: 8,
  late_deduction_per_excess: 0.5,
  half_day_threshold: 0.4,
  full_day_threshold: 0.8,
  probation_period_months: 6,
  el_accrual_per_month: 1.25,
  cl_accrual_per_month: 1.0,
};

function FieldRow({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, children, hint }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

export default function Settings() {
  const [tab, setTab] = useState("company");
  const [company, setCompany] = useState(DEFAULT_COMPANY);
  const [salary, setSalary] = useState(DEFAULT_SALARY);
  const [statutory, setStatutory] = useState(DEFAULT_STATUTORY);
  const [attendance, setAttendance] = useState(DEFAULT_ATTENDANCE);
  const [busy, setBusy] = useState(false);
  const currentYear = new Date().getFullYear();
  const [holYear, setHolYear] = useState(currentYear);
  const [holidays, setHolidays] = useState([]);
  const [newHolDate, setNewHolDate] = useState("");
  const [newHolName, setNewHolName] = useState("");

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      if (data.company) setCompany((s) => ({ ...s, ...data.company }));
      if (data.salary) setSalary((s) => ({ ...s, ...data.salary }));
      if (data.statutory) setStatutory((s) => ({ ...s, ...data.statutory }));
      if (data.attendance) setAttendance((s) => ({ ...s, ...data.attendance }));
    });
  }, []);

  const loadHolidays = async (yr) => {
    try {
      const { data } = await api.get(`/holidays`, { params: { year: yr } });
      setHolidays(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (tab === "holidays") loadHolidays(holYear);
    // eslint-disable-next-line
  }, [tab, holYear]);

  const addHoliday = async () => {
    if (!newHolDate || !newHolName.trim()) {
      toast.error("Both date and name are required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/holidays`, { date: newHolDate, name: newHolName.trim(), applies_to: "all" });
      setNewHolDate("");
      setNewHolName("");
      loadHolidays(holYear);
      toast.success("Holiday added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deleteHoliday = async (id) => {
    if (!window.confirm("Remove this holiday from the calendar?")) return;
    setBusy(true);
    try {
      await api.delete(`/holidays/${id}`);
      loadHolidays(holYear);
      toast.success("Holiday removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const save = async (key, value) => {
    setBusy(true);
    try {
      await api.put(`/settings/${key}`, value);
      toast.success(`${key} saved`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="settings-page" className="space-y-6 max-w-5xl">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400">Phase 4</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Settings
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Company profile, salary structure, statutory rates &amp; attendance rules.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200 p-1 rounded-md w-full overflow-x-auto justify-start">
          <TabsTrigger value="company" data-testid="tab-company">Company</TabsTrigger>
          <TabsTrigger value="salary" data-testid="tab-salary">Salary Structure</TabsTrigger>
          <TabsTrigger value="statutory" data-testid="tab-statutory">Statutory</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-att-rules">Attendance Rules</TabsTrigger>
          <TabsTrigger value="holidays" data-testid="tab-holidays">Holidays</TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="bg-white border border-slate-200 rounded-md p-6 mt-3 space-y-5">
          <FieldRow>
            <Field label="Company Name">
              <Input data-testid="s-company-name" value={company.name} onChange={(e) => setCompany({ ...company, name: e.target.value })} />
            </Field>
            <Field label="GSTIN">
              <Input value={company.gstin} onChange={(e) => setCompany({ ...company, gstin: e.target.value })} />
            </Field>
            <Field label="PAN">
              <Input value={company.pan} onChange={(e) => setCompany({ ...company, pan: e.target.value })} />
            </Field>
            <Field label="TAN">
              <Input value={company.tan} onChange={(e) => setCompany({ ...company, tan: e.target.value })} />
            </Field>
            <Field label="PF Code">
              <Input value={company.pf_code} onChange={(e) => setCompany({ ...company, pf_code: e.target.value })} />
            </Field>
            <Field label="ESI Code">
              <Input value={company.esi_code} onChange={(e) => setCompany({ ...company, esi_code: e.target.value })} />
            </Field>
            <Field label="Establishment ID">
              <Input value={company.establishment_id} onChange={(e) => setCompany({ ...company, establishment_id: e.target.value })} />
            </Field>
            <Field label="Logo URL" hint="Optional — used in payslip header">
              <Input value={company.logo_url} onChange={(e) => setCompany({ ...company, logo_url: e.target.value })} />
            </Field>
          </FieldRow>
          <Field label="Address">
            <Textarea rows={2} value={company.address} onChange={(e) => setCompany({ ...company, address: e.target.value })} />
          </Field>
          <div className="flex justify-end">
            <Button data-testid="save-company" onClick={() => save("company", company)} disabled={busy} className="brand-bg brand-bg-hover text-white">
              <Save className="h-4 w-4 mr-2" /> Save company
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="salary" className="bg-white border border-slate-200 rounded-md p-6 mt-3 space-y-5">
          <FieldRow>
            <Field label="Basic %" hint="Default 50%">
              <Input type="number" step="0.01" value={salary.basic_pct} onChange={(e) => setSalary({ ...salary, basic_pct: Number(e.target.value) })} />
            </Field>
            <Field label="HRA %" hint="Default 30%">
              <Input type="number" step="0.01" value={salary.hra_pct} onChange={(e) => setSalary({ ...salary, hra_pct: Number(e.target.value) })} />
            </Field>
            <Field label="Conveyance %" hint="Default 20%">
              <Input type="number" step="0.01" value={salary.conveyance_pct} onChange={(e) => setSalary({ ...salary, conveyance_pct: Number(e.target.value) })} />
            </Field>
            <Field label="Days in Month (default)" hint="Auto-calculated per month (Jan=31, Feb=28/29, Apr=30, May=31…). Override here only if HR wants a flat divisor for ALL months.">
              <Input type="number" min="28" max="31" value={salary.days_in_month_default} onChange={(e) => setSalary({ ...salary, days_in_month_default: Number(e.target.value) })} />
            </Field>
          </FieldRow>
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
            <strong>Note:</strong> Saved values are read by future runs. Already-approved runs are not affected.
            For now the engine uses the values shown above (50/30/20). Editing here records the change in audit log.
          </div>
          <div className="flex justify-end">
            <Button data-testid="save-salary" onClick={() => save("salary", salary)} disabled={busy} className="brand-bg brand-bg-hover text-white">
              <Save className="h-4 w-4 mr-2" /> Save salary structure
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="statutory" className="bg-white border border-slate-200 rounded-md p-6 mt-3 space-y-5">
          <FieldRow>
            <Field label="PF Wage Ceiling (₹)">
              <Input type="number" value={statutory.pf_wage_cap} onChange={(e) => setStatutory({ ...statutory, pf_wage_cap: Number(e.target.value) })} />
            </Field>
            <Field label="ESI Threshold (₹)">
              <Input type="number" value={statutory.esi_threshold} onChange={(e) => setStatutory({ ...statutory, esi_threshold: Number(e.target.value) })} />
            </Field>
            <Field label="PF Employee %">
              <Input type="number" step="0.01" value={statutory.pf_employee_pct} onChange={(e) => setStatutory({ ...statutory, pf_employee_pct: Number(e.target.value) })} />
            </Field>
            <Field label="EPS %">
              <Input type="number" step="0.01" value={statutory.eps_pct} onChange={(e) => setStatutory({ ...statutory, eps_pct: Number(e.target.value) })} />
            </Field>
            <Field label="EPF %">
              <Input type="number" step="0.01" value={statutory.epf_pct} onChange={(e) => setStatutory({ ...statutory, epf_pct: Number(e.target.value) })} />
            </Field>
            <Field label="PF Admin %">
              <Input type="number" step="0.01" value={statutory.pf_admin_pct} onChange={(e) => setStatutory({ ...statutory, pf_admin_pct: Number(e.target.value) })} />
            </Field>
            <Field label="EDLI %">
              <Input type="number" step="0.01" value={statutory.edli_pct} onChange={(e) => setStatutory({ ...statutory, edli_pct: Number(e.target.value) })} />
            </Field>
            <Field label="ESI Employee %">
              <Input type="number" step="0.01" value={statutory.esi_employee_pct} onChange={(e) => setStatutory({ ...statutory, esi_employee_pct: Number(e.target.value) })} />
            </Field>
            <Field label="ESI Employer %">
              <Input type="number" step="0.01" value={statutory.esi_employer_pct} onChange={(e) => setStatutory({ ...statutory, esi_employer_pct: Number(e.target.value) })} />
            </Field>
          </FieldRow>
          <div className="flex justify-end">
            <Button data-testid="save-statutory" onClick={() => save("statutory", statutory)} disabled={busy} className="brand-bg brand-bg-hover text-white">
              <Save className="h-4 w-4 mr-2" /> Save statutory
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="attendance" className="bg-white border border-slate-200 rounded-md p-6 mt-3 space-y-5">
          <FieldRow>
            <Field label="Late Grace (minutes)" hint="Default 15">
              <Input type="number" value={attendance.late_grace_minutes} onChange={(e) => setAttendance({ ...attendance, late_grace_minutes: Number(e.target.value) })} />
            </Field>
            <Field label="Free Lates / Month" hint="Default 8 (admin only)">
              <Input type="number" value={attendance.free_lates_per_month} onChange={(e) => setAttendance({ ...attendance, free_lates_per_month: Number(e.target.value) })} />
            </Field>
            <Field label="Late Deduction (per excess late)" hint="Default 0.5 day">
              <Input type="number" step="0.1" value={attendance.late_deduction_per_excess} onChange={(e) => setAttendance({ ...attendance, late_deduction_per_excess: Number(e.target.value) })} />
            </Field>
            <Field label="Half-day threshold" hint="Default 0.4 (admin)">
              <Input type="number" step="0.01" value={attendance.half_day_threshold} onChange={(e) => setAttendance({ ...attendance, half_day_threshold: Number(e.target.value) })} />
            </Field>
            <Field label="Full-day threshold" hint="Default 0.8 (admin)">
              <Input type="number" step="0.01" value={attendance.full_day_threshold} onChange={(e) => setAttendance({ ...attendance, full_day_threshold: Number(e.target.value) })} />
            </Field>
            <Field label="Probation period (months)">
              <Input type="number" value={attendance.probation_period_months} onChange={(e) => setAttendance({ ...attendance, probation_period_months: Number(e.target.value) })} />
            </Field>
            <Field label="EL accrual / month" hint="Post-probation (admin)">
              <Input type="number" step="0.01" value={attendance.el_accrual_per_month} onChange={(e) => setAttendance({ ...attendance, el_accrual_per_month: Number(e.target.value) })} />
            </Field>
            <Field label="CL accrual / month" hint="Always (admin)">
              <Input type="number" step="0.01" value={attendance.cl_accrual_per_month} onChange={(e) => setAttendance({ ...attendance, cl_accrual_per_month: Number(e.target.value) })} />
            </Field>
          </FieldRow>
          <div className="flex justify-end">
            <Button data-testid="save-attendance" onClick={() => save("attendance", attendance)} disabled={busy} className="brand-bg brand-bg-hover text-white">
              <Save className="h-4 w-4 mr-2" /> Save attendance rules
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="holidays" className="bg-white border border-slate-200 rounded-md p-6 mt-3 space-y-5">
          <div className="flex items-end gap-3 flex-wrap">
            <Field label="Year">
              <Input
                type="number"
                data-testid="hol-year"
                className="w-28"
                value={holYear}
                min="2020"
                max="2099"
                onChange={(e) => setHolYear(Number(e.target.value))}
              />
            </Field>
            <div className="text-xs text-slate-500 ml-auto">
              {holidays.length} holiday{holidays.length === 1 ? "" : "s"} in {holYear}
            </div>
          </div>

          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Date</th>
                  <th className="text-left px-3 py-2 font-medium">Day</th>
                  <th className="text-left px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {holidays.length === 0 && (
                  <tr>
                    <td colSpan="4" className="text-center py-8 text-slate-400 text-xs">
                      No holidays configured for {holYear}. Add one below.
                    </td>
                  </tr>
                )}
                {holidays.map((h) => {
                  const d = new Date(h.date);
                  const dd = String(d.getDate()).padStart(2, "0");
                  const mm = String(d.getMonth() + 1).padStart(2, "0");
                  const dayName = d.toLocaleDateString("en-IN", { weekday: "long" });
                  return (
                    <tr key={h.id} data-testid={`hol-row-${h.id}`} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium text-slate-900">{dd}-{mm}-{d.getFullYear()}</td>
                      <td className="px-3 py-2 text-slate-600">{dayName}</td>
                      <td className="px-3 py-2 text-slate-700">{h.name}</td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          data-testid={`hol-delete-${h.id}`}
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-red-600 hover:bg-red-50 hover:text-red-700"
                          onClick={() => deleteHoliday(h.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="border-t pt-4 space-y-3">
            <h3 className="text-sm font-semibold text-slate-700">Add holiday</h3>
            <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-3 items-end">
              <Field label="Date">
                <Input
                  type="date"
                  data-testid="new-hol-date"
                  value={newHolDate}
                  onChange={(e) => setNewHolDate(e.target.value)}
                />
              </Field>
              <Field label="Name" hint="e.g., Diwali, Republic Day">
                <Input
                  data-testid="new-hol-name"
                  placeholder="Holiday name"
                  value={newHolName}
                  onChange={(e) => setNewHolName(e.target.value)}
                />
              </Field>
              <Button
                data-testid="add-hol-btn"
                onClick={addHoliday}
                disabled={busy}
                className="brand-bg brand-bg-hover text-white"
              >
                <Plus className="h-4 w-4 mr-2" /> Add
              </Button>
            </div>
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-3 leading-relaxed">
              <strong>How it works:</strong> When you Finalize an attendance month, every configured
              holiday in that month is auto-marked as <strong>Paid Holiday (P)</strong> for all employees.
              If an employee actually worked on a holiday, the entire worked time is credited as OT
              for OT-eligible blue-collar staff. Manual day overrides (set in the Review screen) always
              take precedence.
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
